// InventoryPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Plus, Edit, Trash2, Upload, AlertTriangle,
  Package, Search, Filter, RefreshCw, Download,
  ChevronLeft, ChevronRight, Eye
} from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const InventoryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State management
  const [products, setProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Store ID from URL or localStorage
  const [storeId, setStoreId] = useState(() => {
    // Get storeId from localStorage or use the first one from user
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedData = JSON.parse(userData);
      return parsedData.storeIds?.[0] || null;
    }
    return null;
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStockUpdateModal, setShowStockUpdateModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Form states
  const [productForm, setProductForm] = useState({
    name: '',
    productCode: '',
    category: '',
    currentStock: 0,
    unit: 'PCS',
    costPrice: 0,
    sellingPrice: 0,
    supplierName: '',
    minStockLevel: 1,
    storageLocation: '',
    isActive: true,
    description: '',
    taxRate: 0
  });

  const [stockUpdateQuantity, setStockUpdateQuantity] = useState(0);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);

  // Validate storeId on component mount
  useEffect(() => {
    if (!storeId || !user?.storeIds.includes(parseInt(storeId))) {
      console.log('Invalid storeId or unauthorized:', { storeId, userStoreIds: user?.storeIds });
      navigate('/unauthorized');
      return;
    }
  }, [storeId, user, navigate]);

  // Fetch products - FIXED URL
  const fetchProducts = async () => {
    if (!storeId) return;

    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching products with token:', token ? 'Token present' : 'No token');

      const response = await api.get(`/products?page=${currentPage}&size=${pageSize}&sortBy=${sortBy}&sortDir=${sortDir}&storeId=${storeId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      console.log('Products response:', response.data);

      if (response.data.success) {
        setProducts(response.data.data.content);
        setTotalPages(response.data.data.totalPages);
        setTotalElements(response.data.data.totalElements);
      }
    } catch (err) {
      console.error('Fetch products error:', err);
      if (err.response?.status === 401) {
        console.log('Unauthorized - redirecting to login');
        navigate('/login');
      }
      setError(err.response?.data?.message || err.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  // Fetch low stock products - FIXED URL
  const fetchLowStockProducts = async () => {
    if (!storeId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/products/low-stock?storeId=${storeId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      console.log('Low stock response:', response.data);

      if (response.data.success) {
        setLowStockProducts(response.data.data);
      }
    } catch (err) {
      console.error('Fetch low stock error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  // Create product - FIXED VERSION
  const createProduct = async () => {
    if (!storeId) {
      setError('No store selected');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Prepare the request data matching the curl example
      const requestData = {
        name: productForm.name,
        productCode: productForm.productCode,
        category: productForm.category,
        currentStock: productForm.currentStock,
        unit: productForm.unit,
        costPrice: productForm.costPrice,
        sellingPrice: productForm.sellingPrice,
        supplierName: productForm.supplierName,
        minStockLevel: productForm.minStockLevel,
        storageLocation: productForm.storageLocation,
        isActive: productForm.isActive,
        // Optional fields from curl example
        description: productForm.description,
        taxRate: productForm.taxRate
      };

      console.log('Creating product with data:', requestData);

      const response = await api.post('/products', requestData, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'application/json',
        },
      });

      console.log('Create product response:', response.data);

      if (response.data.success) {
        setSuccessMessage('Product created successfully!');
        fetchProducts();
        fetchLowStockProducts();
        resetForm();
        setShowCreateModal(false);
      } else {
        setError(response.data.message || 'Failed to create product');
      }
    } catch (err) {
      console.error('Create product error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 400) {
        setError(err.response?.data?.message || 'Invalid product data. Please check all fields.');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to create product');
      }
    }
  };

  // Update product - FIXED URL
  const updateProduct = async () => {
    if (!storeId || !selectedProduct) {
      setError('No store selected or product not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      // Prepare the request data
      const requestData = {
        name: productForm.name,
        productCode: productForm.productCode,
        category: productForm.category,
        currentStock: productForm.currentStock,
        unit: productForm.unit,
        costPrice: productForm.costPrice,
        sellingPrice: productForm.sellingPrice,
        supplierName: productForm.supplierName,
        minStockLevel: productForm.minStockLevel,
        storageLocation: productForm.storageLocation,
        isActive: productForm.isActive,
        description: productForm.description,
        taxRate: productForm.taxRate
      };

      console.log('Updating product with data:', {
        productId: selectedProduct.id,
        storeId,
        requestData
      });

      const response = await api.put(`/products/${selectedProduct.id}?storeId=${storeId}`, requestData, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'application/json',
        },
      });

      console.log('Update product response:', response.data);

      if (response.data.success) {
        setSuccessMessage('Product updated successfully!');
        fetchProducts();
        fetchLowStockProducts();
        resetForm();
        setShowEditModal(false);
      } else {
        setError(response.data.message || 'Failed to update product');
      }
    } catch (err) {
      console.error('Update product error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to update product');
      }
    }
  };

  // Delete product - FIXED URL
  const deleteProduct = async () => {
    if (!storeId || !selectedProduct) {
      setError('No store selected or product not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await api.delete(`/products/${selectedProduct.id}?storeId=${storeId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      console.log('Delete product response:', response);

      setSuccessMessage('Product deleted successfully!');
      fetchProducts();
      fetchLowStockProducts();
      setShowDeleteModal(false);
      setSelectedProduct(null);
    } catch (err) {
      console.error('Delete product error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
      setError(err.response?.data?.message || err.message || 'Failed to delete product');
    }
  };

  // Update stock - FIXED URL
  const updateStock = async () => {
    if (!storeId || !selectedProduct) {
      setError('No store selected or product not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await api.put(`/products/${selectedProduct.id}/stock?storeId=${storeId}&quantity=${stockUpdateQuantity}`, {}, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'application/json',
        },
      });

      console.log('Update stock response:', response.data);

      if (response.data.success) {
        setSuccessMessage('Stock updated successfully!');
        fetchProducts();
        fetchLowStockProducts();
        setStockUpdateQuantity(0);
        setShowStockUpdateModal(false);
        setSelectedProduct(null);
      }
    } catch (err) {
      console.error('Update stock error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
      setError(err.response?.data?.message || err.message || 'Failed to update stock');
    }
  };

  // Bulk upload - FIXED URL
  const handleBulkUpload = async () => {
    if (!storeId) {
      setError('No store selected');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', bulkUploadFile);

      const response = await api.post(`/products/bulk-upload?storeId=${storeId}`, formData, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Bulk upload response:', response.data);

      if (response.data.success) {
        setSuccessMessage('Products uploaded successfully!');
        fetchProducts();
        setShowBulkUploadModal(false);
        setBulkUploadFile(null);
      } else {
        setError(response.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Bulk upload error:', err);
      setError(err.response?.data?.message || err.message || 'Bulk upload failed');
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setProductForm({
      name: '',
      productCode: '',
      category: '',
      currentStock: 0,
      unit: 'PCS',
      costPrice: 0,
      sellingPrice: 0,
      supplierName: '',
      minStockLevel: 1,
      storageLocation: '',
      isActive: true,
      description: '',
      taxRate: 0
    });
    setSelectedProduct(null);
  };

  // Open edit modal with product data
  const openEditModal = (product) => {
    setSelectedProduct(product);
    setProductForm({
      name: product.name,
      productCode: product.productCode,
      category: product.category,
      currentStock: product.currentStock,
      unit: product.unit,
      costPrice: product.costPrice,
      sellingPrice: product.sellingPrice,
      supplierName: product.supplierName || '',
      minStockLevel: product.minStockLevel,
      storageLocation: product.storageLocation || '',
      isActive: product.isActive,
      description: product.description || '',
      taxRate: product.taxRate || 0
    });
    setShowEditModal(true);
  };

  // Open delete confirmation
  const openDeleteModal = (product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  // Open stock update modal
  const openStockUpdateModal = (product) => {
    setSelectedProduct(product);
    setStockUpdateQuantity(product.currentStock);
    setShowStockUpdateModal(true);
  };

  // Handle bulk upload file change
  const handleFileChange = (e) => {
    setBulkUploadFile(e.target.files[0]);
  };

  // Download sample CSV
  const downloadSampleCSV = () => {
    const csvContent = "name,productCode,category,currentStock,unit,costPrice,sellingPrice,supplierName,minStockLevel,storageLocation,description,taxRate\nLaptop Dell,LP001,Electronics,5,PCS,40000,55000,TechSupply,2,Shelf A,High performance laptop,0";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-products.csv';
    a.click();
  };

  // Search functionality
  const handleSearch = () => {
    setCurrentPage(0); // Reset to first page when searching
    // Implement search filter here if your backend supports it
    fetchProducts();
  };

  // Initial fetch
  useEffect(() => {
    if (storeId && user?.storeIds.includes(parseInt(storeId))) {
      fetchProducts();
      fetchLowStockProducts();
    }
  }, [currentPage, pageSize, sortBy, sortDir, storeId, user]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  // Calculate statistics
  const totalStockValue = products.reduce((sum, product) =>
    sum + (product.currentStock * product.costPrice), 0);

  const totalSellingValue = products.reduce((sum, product) =>
    sum + (product.currentStock * product.sellingPrice), 0);

  const lowStockCount = products.filter(product =>
    product.currentStock <= product.minStockLevel).length;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Inventory Management</h1>
            <p className="text-gray-600 mt-1">Store ID: {storeId} | Manage your store products and stock levels</p>
          </div>
          <div className="flex items-center space-x-3 mt-4 md:mt-0">
            <button
              onClick={() => setShowBulkUploadModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <Package className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Products</p>
                <p className="text-2xl font-bold">{totalElements}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Low Stock</p>
                <p className="text-2xl font-bold">{lowStockCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 font-bold">₹</span>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Stock Value (Cost)</p>
                <p className="text-xl font-bold">₹{totalStockValue.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-purple-600 font-bold">₹</span>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Selling Value</p>
                <p className="text-xl font-bold">₹{totalSellingValue.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Low Stock Warning */}
      {lowStockProducts.length > 0 && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <h3 className="font-semibold text-red-800">Low Stock Alert</h3>
          </div>
          <p className="text-red-600 text-sm mt-1">
            {lowStockProducts.length} product(s) are below minimum stock level
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {lowStockProducts.map(product => (
              <span key={product.id} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                {product.name} ({product.currentStock} left)
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-3 md:space-y-0">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search products by name, code, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="name">Sort by Name</option>
              <option value="currentStock">Sort by Stock</option>
              <option value="sellingPrice">Sort by Price</option>
              <option value="createdAt">Sort by Date</option>
            </select>

            <select
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>

            <button
              onClick={fetchProducts}
              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading products...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stock
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cost Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Selling Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{product.name}</div>
                          {product.storageLocation && (
                            <div className="text-sm text-gray-500">
                              Location: {product.storageLocation}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {product.productCode}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">{product.category}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <span className={`font-medium ${product.currentStock <= product.minStockLevel
                              ? 'text-red-600'
                              : 'text-gray-900'
                            }`}>
                            {product.currentStock} {product.unit}
                          </span>
                          {product.currentStock <= product.minStockLevel && (
                            <AlertTriangle className="w-4 h-4 text-red-500 ml-2" />
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          Min: {product.minStockLevel}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium">₹{product.costPrice.toLocaleString('en-IN')}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium text-green-600">
                          ₹{product.sellingPrice.toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                          }`}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openEditModal(product)}
                            className="p-1 text-blue-600 hover:text-blue-800"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openStockUpdateModal(product)}
                            className="p-1 text-purple-600 hover:text-purple-800"
                            title="Update Stock"
                          >
                            <Package className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(product)}
                            className="p-1 text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {products.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-400 mx-auto" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No products found</h3>
                <p className="mt-1 text-gray-500">Get started by adding your first product.</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      {products.length > 0 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage * pageSize) + 1}</span> to{' '}
            <span className="font-medium">
              {Math.min((currentPage + 1) * pageSize, totalElements)}
            </span>{' '}
            of <span className="font-medium">{totalElements}</span> results
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className={`p-2 rounded-lg ${currentPage === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-300 hover:bg-gray-50'
                }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i;
              } else if (currentPage <= 2) {
                pageNum = i;
              } else if (currentPage >= totalPages - 3) {
                pageNum = totalPages - 5 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 rounded-lg ${currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage === totalPages - 1}
              className={`p-2 rounded-lg ${currentPage === totalPages - 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-300 hover:bg-gray-50'
                }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Create Product Modal - ADDED THIS MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Add New Product</h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Code *
                  </label>
                  <input
                    type="text"
                    value={productForm.productCode}
                    onChange={(e) => setProductForm({ ...productForm, productCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <input
                    type="text"
                    value={productForm.category}
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit *
                  </label>
                  <select
                    value={productForm.unit}
                    onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="PCS">Pieces</option>
                    <option value="KG">Kilograms</option>
                    <option value="L">Liters</option>
                    <option value="M">Meters</option>
                    <option value="BOX">Box</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost Price (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.costPrice}
                    onChange={(e) => setProductForm({ ...productForm, costPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Selling Price (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.sellingPrice}
                    onChange={(e) => setProductForm({ ...productForm, sellingPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Stock *
                  </label>
                  <input
                    type="number"
                    value={productForm.currentStock}
                    onChange={(e) => setProductForm({ ...productForm, currentStock: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Stock Level *
                  </label>
                  <input
                    type="number"
                    value={productForm.minStockLevel}
                    onChange={(e) => setProductForm({ ...productForm, minStockLevel: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier Name
                  </label>
                  <input
                    type="text"
                    value={productForm.supplierName}
                    onChange={(e) => setProductForm({ ...productForm, supplierName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Storage Location
                  </label>
                  <input
                    type="text"
                    value={productForm.storageLocation}
                    onChange={(e) => setProductForm({ ...productForm, storageLocation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.taxRate}
                    onChange={(e) => setProductForm({ ...productForm, taxRate: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="createIsActive"
                    checked={productForm.isActive}
                    onChange={(e) => setProductForm({ ...productForm, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label htmlFor="createIsActive" className="ml-2 text-sm text-gray-700">
                    Active Product
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={createProduct}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Product
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Edit Product</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Code *
                  </label>
                  <input
                    type="text"
                    value={productForm.productCode}
                    onChange={(e) => setProductForm({ ...productForm, productCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <input
                    type="text"
                    value={productForm.category}
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit *
                  </label>
                  <select
                    value={productForm.unit}
                    onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="PCS">Pieces</option>
                    <option value="KG">Kilograms</option>
                    <option value="L">Liters</option>
                    <option value="M">Meters</option>
                    <option value="BOX">Box</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost Price (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.costPrice}
                    onChange={(e) => setProductForm({ ...productForm, costPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Selling Price (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.sellingPrice}
                    onChange={(e) => setProductForm({ ...productForm, sellingPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Stock *
                  </label>
                  <input
                    type="number"
                    value={productForm.currentStock}
                    onChange={(e) => setProductForm({ ...productForm, currentStock: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Stock Level *
                  </label>
                  <input
                    type="number"
                    value={productForm.minStockLevel}
                    onChange={(e) => setProductForm({ ...productForm, minStockLevel: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier Name
                  </label>
                  <input
                    type="text"
                    value={productForm.supplierName}
                    onChange={(e) => setProductForm({ ...productForm, supplierName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Storage Location
                  </label>
                  <input
                    type="text"
                    value={productForm.storageLocation}
                    onChange={(e) => setProductForm({ ...productForm, storageLocation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.taxRate}
                    onChange={(e) => setProductForm({ ...productForm, taxRate: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="editIsActive"
                    checked={productForm.isActive}
                    onChange={(e) => setProductForm({ ...productForm, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label htmlFor="editIsActive" className="ml-2 text-sm text-gray-700">
                    Active Product
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={updateProduct}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update Product
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Delete Product</h3>
                  <p className="text-gray-600">This action cannot be undone</p>
                </div>
              </div>

              <p className="mb-6">
                Are you sure you want to delete <span className="font-semibold">{selectedProduct.name}</span> ({selectedProduct.productCode})?
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteProduct}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete Product
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Update Modal */}
      {showStockUpdateModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Update Stock Level</h3>
                <button
                  onClick={() => setShowStockUpdateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 mb-2">Product: <span className="font-semibold">{selectedProduct.name}</span></p>
                <p className="text-gray-600">Current Stock: <span className="font-semibold">{selectedProduct.currentStock} {selectedProduct.unit}</span></p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Stock Quantity
                </label>
                <input
                  type="number"
                  value={stockUpdateQuantity}
                  onChange={(e) => setStockUpdateQuantity(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowStockUpdateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={updateStock}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update Stock
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Bulk Upload Products</h3>
                <button
                  onClick={() => setShowBulkUploadModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start">
                  <Upload className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium mb-1">CSV Format Required</p>
                    <p className="text-xs text-blue-700">
                      Your CSV should include columns: name, productCode, category, currentStock, unit, costPrice, sellingPrice, supplierName, minStockLevel, storageLocation, description, taxRate
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload CSV File
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    {bulkUploadFile ? (
                      <div>
                        <Package className="w-12 h-12 text-green-600 mx-auto" />
                        <p className="mt-2 font-medium">{bulkUploadFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {(bulkUploadFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                        <p className="mt-2 text-gray-600">Click to upload CSV file</p>
                        <p className="text-sm text-gray-500">or drag and drop</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={downloadSampleCSV}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Sample CSV
                </button>

                {bulkUploadFile && (
                  <button
                    onClick={() => setBulkUploadFile(null)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Remove File
                  </button>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowBulkUploadModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={!bulkUploadFile}
                  className={`px-4 py-2 rounded-lg ${bulkUploadFile
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                >
                  Upload Products
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default InventoryPage;